import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getAppPayouts, getAppPayoutStats, updateAppPayoutStatus } from '../services/api';

const statusBadge = s => ({ completed:'badge badge-green', pending:'badge badge-amber', processing:'badge badge-blue', rejected:'badge badge-red' })[s] || 'badge badge-gray';

export default function Payouts({ toast }) {
  const { token } = useApp();
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]     = useState(0);

  const load = async (p=1) => {
    setLoading(true);
    try {
      const [pr, sr] = await Promise.allSettled([
        getAppPayouts(token, { page:p, limit:20 }),
        getAppPayoutStats(token),
      ]);
      if (pr.status==='fulfilled') {
        const d = pr.value.data || pr.value;
        // Handle both response formats
        const list = d.payouts || d.data || d || [];
        setPayouts(Array.isArray(list) ? list : []);
        setTotal(d.total || pr.value.pagination?.total || 0);
        setTotalPages(d.pages || d.totalPages || pr.value.pagination?.totalPages || 1);
      }
      if (sr.status==='fulfilled') setStats(sr.value.data || sr.value);
    } catch(e) { toast?.(e.message,'error'); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ load(); },[token]);

  const updateStatus = async (id, status, note='') => {
    try { await updateAppPayoutStatus(token, id, status, note); toast?.(`Payout ${status}`); load(page); }
    catch(e) { toast?.(e.message,'error'); }
  };

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Pending</div><div className="stat-value">{stats?.pending||0}</div><div className="stat-sub">Awaiting approval</div></div>
        <div className="stat-card"><div className="stat-label">Processing</div><div className="stat-value">{stats?.processing||0}</div><div className="stat-sub">In progress</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value">{stats?.completed||0}</div><div className="stat-sub stat-up">Paid out</div></div>
        <div className="stat-card"><div className="stat-label">Total paid</div><div className="stat-value">${parseFloat(stats?.totalPaidOut||0).toFixed(2)}</div><div className="stat-sub stat-up">All time</div></div>
      </div>
      <div className="card" style={{ padding:0 }}>
        <div className="card-header" style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          <span className="card-title">Payout requests</span>
          <span style={{ fontSize:12, color:'var(--text-dim)' }}>{total} total</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Scout</th><th>Amount</th><th>Method</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'var(--text-dim)' }}>Loading payouts…</td></tr>
              ) : payouts.length===0 ? (
                <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'var(--text-dim)' }}>No payout requests yet</td></tr>
              ) : payouts.map(p=>(
                <tr key={p.id}>
                  <td><div style={{ fontWeight:500 }}>{p.scoutName||p.scoutId||'—'}</div><div style={{ fontSize:11, color:'var(--text-dim)' }}>{p.scoutEmail||''}</div></td>
                  <td style={{ fontWeight:600, color:'var(--accent)' }}>${parseFloat(p.amount||0).toFixed(2)}</td>
                  <td style={{ fontSize:12 }}>{p.paymentMethod||'—'}</td>
                  <td><span className={statusBadge(p.status)}>{p.status}</span></td>
                  <td style={{ fontSize:12, color:'var(--text-dim)' }}>{p.createdAt?new Date(p.createdAt).toLocaleDateString():'—'}</td>
                  <td>
                    {p.status==='pending' && (
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-primary" style={{ fontSize:11, padding:'3px 10px' }} onClick={()=>updateStatus(p.id,'completed','Approved by admin')}>Approve</button>
                        <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 10px', color:'var(--danger)' }} onClick={()=>updateStatus(p.id,'rejected','Rejected by admin')}>Reject</button>
                      </div>
                    )}
                    {p.status==='processing' && <button className="btn btn-primary" style={{ fontSize:11 }} onClick={()=>updateStatus(p.id,'completed','Marked as paid')}>Mark paid</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages>1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={()=>{setPage(p=>p-1);load(page-1);}}>← Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {page} / {totalPages}</span>
          <button className="btn btn-ghost" disabled={page>=totalPages} onClick={()=>{setPage(p=>p+1);load(page+1);}}>Next →</button>
        </div>
      )}
    </div>
  );
}
